/**
 * The basic poses every figure has: standing, walking, the qinggong run, the fighting stance,
 * sitting, putting the weapon away and drawing it, and the 'motion' and 'gaze' poses that stand for
 * a frame of a motion or a look toward someone.
 */
import { decodeGaze } from '../rig/gaze';
import {
  FRAMES,
  gait,
  SHEATHE_FRAMES,
  WALK_FRAMES,
  type Rig,
  type NearArm,
  type LimbSpec,
  type PoseRigs,
} from './kit';
import { rigFor, motionKeys, type Pose } from './index';

/**
 * The step (0 to `SHEATHE_FRAMES` − 1, counted as putting away) of a frame that puts a weapon away
 * or draws it, standing or walking; null for any other pose.
 */
export function stowPhase(pose: Pose, ph: number): number | null {
  const step =
    pose === 'sheathe' || pose === 'unsheathe'
      ? ph % SHEATHE_FRAMES
      : pose === 'walkSheathe' || pose === 'walkUnsheathe'
        ? Math.floor(ph / FRAMES) % SHEATHE_FRAMES
        : null;
  if (step === null) return null;
  return pose === 'sheathe' || pose === 'walkSheathe' ? step : SHEATHE_FRAMES - 1 - step;
}

/**
 * Where the weapon arm is at step `ph` of putting the weapon away: raised at the guard, brought
 * across before the chest, down to the hip, held there while the blade slides home, then let fall to
 * the side — each step part of the way from one of those positions to the next.
 */
function sheatheArm(ph: number): readonly [NearArm, NearArm?, number?] {
  const u = ph / (SHEATHE_FRAMES - 1);
  const keys: readonly [number, NearArm][] = [
    [0, 'guard'],
    [0.28, 'chest'],
    [0.5, 'hip'],
    [0.78, 'hip'],
    [1, 'hang'],
  ];
  for (let i = 0; i < keys.length - 1; i++) {
    const [u0, a] = keys[i]!;
    const [u1, b] = keys[i + 1]!;
    if (u > u1 && i < keys.length - 2) continue;
    const t = Math.max(0, Math.min(1, (u - u0) / (u1 - u0)));
    return a === b || t === 0 ? [a] : t === 1 ? [b] : [a, b, t];
  }
  return ['hang'];
}

const walkStow = (ph: number, pose: 'walkSheathe' | 'walkUnsheathe'): Partial<Rig> => {
  // Putting the weapon away (or drawing it) without breaking stride: the walk's legs and body,
  // the weapon arm on its way through the same steps as standing.
  const [arm, armTo, armT] = sheatheArm(stowPhase(pose, ph)!);
  return { ...rigFor('walk', ph % FRAMES), armSwing: 0, arm, ...(armTo && { armTo, armT }) };
};

/** This group's poses. */
export type Poses =
  | 'motion'
  | 'sheathe'
  | 'unsheathe'
  | 'walkSheathe'
  | 'walkUnsheathe'
  | 'idle'
  | 'walk'
  | 'sprint'
  | 'ready'
  | 'sit'
  | 'gaze';

export const POSES: PoseRigs<Poses> = {
  motion: (ph) => {
    // Asked for alone (the skeleton blends whole skeletons instead): the nearer key's numbers.
    const { a, b, t } = motionKeys(ph);
    return rigFor(t < 0.5 ? a : b, 0);
  },
  sheathe: (ph) => {
    // The weapon raised, brought across before the chest, down to the hip and slid
    // home (or onto the back, into the belt), the hand let go and fallen to the side. The
    // in-between steps carry the arm part of the way from one position to the next.
    const [arm, armTo, armT] = sheatheArm(ph % SHEATHE_FRAMES);
    return { f1: -2, f2: 1, arm, ...(armTo && { armTo, armT }) };
  },
  unsheathe: (ph) => {
    // The same, backwards: the hand to the hilt, the weapon drawn up across the chest to the guard.
    const [arm, armTo, armT] = sheatheArm(SHEATHE_FRAMES - 1 - (ph % SHEATHE_FRAMES));
    return { f1: -2, f2: 1, arm, ...(armTo && { armTo, armT }) };
  },
  walkSheathe: walkStow,
  walkUnsheathe: walkStow,
  idle: (ph) => {
    // At ease: feet a little apart, the weight settled, standing still — only the hair and hems
    // stir. (The whole body dropping a row to breathe read as a flinch, as if struck.)
    return { f1: -2, f2: 1, sway: Math.sin((ph / FRAMES) * Math.PI * 2) * 0.5 };
  },
  walk: (ph) => {
    // A walk: each foot is down for six tenths of the stride, so both are on the ground for a
    // moment as one lands and the other pushes off; the swinging foot only clears the ground, at
    // its highest mid-swing. The body is lowest as the feet land apart and highest as the legs
    // pass, carried upright, and the near arm swings against the near leg.
    const { f1, l1, f2, l2, o } = gait(ph / WALK_FRAMES, 6, 0.6, 2.5, 1, 2);
    return {
      f1,
      f2,
      l1,
      l2,
      o,
      lean: 1,
      sway: 1.2 + Math.cos((ph / WALK_FRAMES) * Math.PI * 4) * 0.5,
      armSwing: Math.round(f1 * 0.6) || 0,
    };
  },
  sprint: (ph) => {
    // Qinggong run: longer, lower bounds, body pitched forward, arms and sleeves streaming behind.
    const { f1, l1, f2, l2 } = gait(ph / WALK_FRAMES, 10, 0.3, 7);
    // Lowest as each foot is under the body mid-stance (15% and 65% of the cycle), highest in the
    // flight between: a smooth bound rather than the walk's jolt off the planted foot.
    const bound = 0.5 + 0.5 * Math.cos((ph / WALK_FRAMES - 0.15) * Math.PI * 4);
    return {
      f1,
      f2,
      l1,
      l2,
      o: Math.round(0.5 + 2 * bound),
      lean: 2,
      tilt: 16,
      hem: 1,
      sway: 2.6 + Math.cos((ph / WALK_FRAMES) * Math.PI * 4) * 0.6,
      // The trailing arms still pump with the stride (the blade rocks with them, see `weaponAim`).
      armSwing: Math.round(f1 * 0.35 * 10) / 10,
      arm: 'back',
      far: 'back',
      face: 'block',
    };
  },
  ready: (ph) => {
    // Fighting stance between blows: sunk low on bent knees, feet wide, blade levelled at the
    // enemy. The hips stay put; the chest rises and falls with the breath (see `skeleton`).
    return {
      o: 3,
      lean: 1,
      tilt: 5,
      f1: -6,
      f2: 5,
      // Elbow bent, the hand before the waist, the point raised at the foe's throat (中段); the off hand's sword
      // fingers forward (lane C0-WEAPON: the old straight arm with the blade dropped read as a stick).
      arm: 'readyBlade',
      far: 'fwd',
      sway: Math.sin((ph / FRAMES) * Math.PI * 2) * 0.5,
    };
  },
  sit: () => {
    return { o: 12, arm: 'sit', closed: true };
  },
  gaze: (ph) => {
    // Asked for alone (the skeleton turns the eyes and head itself, rig/gaze.ts): the base pose.
    const { base, frame } = decodeGaze(ph);
    return rigFor(base, frame);
  },
};

export const ARMS = {
  /** The fighting stance's blade (lane C0-WEAPON): elbow bent, hand at the waist, point up at the foe. */
  readyBlade: { hand: [8, 31], ang: -0.5, front: true },
  back: { hand: [-6, 35], ang: Math.PI - 0.35, front: false },
  out: { hand: [12, 22], ang: 0.6, front: true },
  flail: { hand: [-5, 29], ang: Math.PI + 0.45, front: false },
} satisfies Record<string, LimbSpec>;
